import type { PlatformError } from "@situate/contracts/platform";

/**
 * API error wrapping RFC 7807 Problem Details responses.
 *
 * Parses PlatformError from platform-api and runtime-api responses.
 */
export class ApiError extends Error {
  public readonly statusCode: number;
  public readonly type: string;
  public readonly detail?: string;
  public readonly traceId?: string;
  public readonly requestId?: string;

  constructor(message: string, statusCode: number, platformError?: Partial<PlatformError>) {
    super(message);
    this.name = "ApiError";
    this.statusCode = statusCode;
    this.type = platformError?.type ?? "about:blank";
    this.detail = platformError?.detail;
    this.traceId = platformError?.trace_id;
    this.requestId = platformError?.request_id;
  }

  /**
   * Error code derived from the RFC 7807 type URI.
   * For backwards compatibility with code that checks error.code.
   */
  get code(): string {
    return this.type;
  }

  static fromResponse(statusCode: number, body: unknown): ApiError {
    // Try to parse RFC 7807 PlatformError format
    if (typeof body === "object" && body !== null) {
      const platformError = body as Partial<PlatformError> & { message?: unknown };

      // RFC 7807 uses `title`/`detail`; NestJS errors use `message`.
      const message =
        platformError.title ||
        platformError.detail ||
        (typeof platformError.message === "string" ? platformError.message : undefined) ||
        "Request failed";

      return new ApiError(message, statusCode, platformError);
    }

    return new ApiError("Request failed", statusCode);
  }
}
